import { Show, SignInButton, SignUpButton, useUser, UserButton } from "@clerk/tanstack-react-start";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Menu, User } from "lucide-react";
import { useEffect } from "react";
import { FaDiscord } from "react-icons/fa6";

import { DiscordInviteButton } from "#/components/DiscordInviteButton";
import { Button } from "#/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "#/components/ui/dropdown-menu";
import { Skeleton } from "#/components/ui/skeleton";
import { clearAnonymousPlayerIdClient } from "#/lib/anonymousPlayerId";
import { DISCORD_INVITE_URL } from "#/lib/discordInvite";
import { myEntitlementsQueryOptions } from "#/lib/queries";

function ClearAnonymousPlayerIdOnSignIn() {
  useEffect(() => {
    clearAnonymousPlayerIdClient();
  }, []);
  return null;
}

function useShowHeaderUpgrade() {
  const { user } = useUser();
  const entitlementsQuery = useQuery({
    ...myEntitlementsQueryOptions(user?.id ?? ""),
    enabled: Boolean(user?.id),
  });

  if (!user?.id || entitlementsQuery.isPending || entitlementsQuery.isError) {
    return false;
  }
  return entitlementsQuery.data.tier !== "premium";
}

function HeaderUpgradeLink() {
  const showUpgrade = useShowHeaderUpgrade();
  if (!showUpgrade) return null;

  return (
    <Link to="/premium" preload={false}>
      <Button variant="premium" size="sm" className="text-sm font-semibold">
        Upgrade
      </Button>
    </Link>
  );
}

function HeaderUpgradeMenuItem() {
  const showUpgrade = useShowHeaderUpgrade();
  if (!showUpgrade) return null;

  return (
    <DropdownMenuItem
      className="min-h-10 gap-2 rounded-md bg-premium px-3 py-2.5 text-sm font-medium text-premium-foreground focus:bg-premium/80 focus:text-premium-foreground"
      render={<Link to="/premium" preload={false} />}
    >
      Upgrade
    </DropdownMenuItem>
  );
}

function HeaderBrandLink() {
  return (
    <Link
      to="/"
      className="flex shrink-0 items-center gap-2 text-sm font-bold whitespace-nowrap text-foreground no-underline"
    >
      <span className="flex h-6 w-6 items-center justify-center border border-primary bg-primary text-xs font-black text-primary-foreground">
        DW
      </span>
      Daily Wars
    </Link>
  );
}

function HeaderDiscordInviteButton() {
  return <DiscordInviteButton label="Join Discord" className="text-sm font-semibold" />;
}

function HeaderDiscordInviteMenuItem() {
  return (
    <DropdownMenuItem
      className="min-h-10 gap-2 rounded-md bg-[#5865F2] px-3 py-2.5 text-sm font-medium text-white focus:bg-[#5865F2]/90 focus:text-white"
      render={<a href={DISCORD_INVITE_URL} target="_blank" rel="noopener noreferrer" />}
    >
      <FaDiscord aria-hidden="true" />
      Join Discord
    </DropdownMenuItem>
  );
}

function HeaderPrimaryNav() {
  return (
    <div className="hidden shrink-0 flex-nowrap items-center gap-4 text-sm font-semibold whitespace-nowrap sm:flex sm:gap-5">
      <Link
        to="/archive"
        className="text-muted-foreground transition-colors hover:text-foreground"
        activeProps={{ className: "text-foreground" }}
      >
        Archive
      </Link>
      <Link
        to="/stats"
        className="text-muted-foreground transition-colors hover:text-foreground"
        activeProps={{ className: "text-foreground" }}
      >
        Stats
      </Link>
      <Link
        to="/hiscores"
        className="text-muted-foreground transition-colors hover:text-foreground"
        activeProps={{ className: "text-foreground" }}
      >
        Hiscores
      </Link>
      <Link
        to="/codex"
        className="text-muted-foreground transition-colors hover:text-foreground"
        activeProps={{ className: "text-foreground" }}
      >
        Guide
      </Link>
      <Show when="signed-in">
        <Link
          to="/profile"
          className="text-muted-foreground transition-colors hover:text-foreground"
          activeProps={{ className: "text-foreground" }}
        >
          Profile
        </Link>
      </Show>
      <HeaderDiscordInviteButton />
      <Show when="signed-in">
        <HeaderUpgradeLink />
      </Show>
    </div>
  );
}

function HeaderMobilePrimaryLinks() {
  return (
    <>
      <DropdownMenuItem
        className="min-h-10 gap-2 rounded-md px-3 py-2.5 text-sm font-medium"
        render={<Link to="/archive" />}
      >
        Archive
      </DropdownMenuItem>
      <DropdownMenuItem
        className="min-h-10 gap-2 rounded-md px-3 py-2.5 text-sm font-medium"
        render={<Link to="/stats" />}
      >
        Stats
      </DropdownMenuItem>
      <DropdownMenuItem
        className="min-h-10 gap-2 rounded-md px-3 py-2.5 text-sm font-medium"
        render={<Link to="/hiscores" />}
      >
        Hiscores
      </DropdownMenuItem>
      <DropdownMenuItem
        className="min-h-10 gap-2 rounded-md px-3 py-2.5 text-sm font-medium"
        render={<Link to="/codex" />}
      >
        Guide
      </DropdownMenuItem>
    </>
  );
}

function HeaderMobileMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="outline"
            size="icon"
            className="sm:hidden"
            aria-label="Open navigation menu"
          >
            <Menu />
          </Button>
        }
      />
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-56 min-w-56 p-2 shadow-lg sm:hidden"
      >
        <HeaderMobilePrimaryLinks />
        <Show when="signed-in">
          <DropdownMenuItem
            className="min-h-10 gap-2 rounded-md px-3 py-2.5 text-sm font-medium"
            render={<Link to="/profile" />}
          >
            Profile
          </DropdownMenuItem>
        </Show>
        <HeaderDiscordInviteMenuItem />
        <Show when="signed-in">
          <HeaderUpgradeMenuItem />
        </Show>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HeaderAccountControls() {
  return (
    <div className="ml-auto flex shrink-0 items-center gap-2">
      <Show when="signed-in">
        <ClearAnonymousPlayerIdOnSignIn />
        <UserButton fallback={<Skeleton className="size-7 shrink-0 rounded-full" aria-hidden />}>
          <UserButton.MenuItems>
            <UserButton.Link href="/profile" label="Profile" labelIcon={<User size={16} />} />
          </UserButton.MenuItems>
        </UserButton>
      </Show>
      <Show when="signed-out">
        <SignInButton>
          <Button>Sign in</Button>
        </SignInButton>
        <SignUpButton>
          <Button variant="outline" className="hidden sm:inline-flex">
            Sign up
          </Button>
        </SignUpButton>
      </Show>
      <HeaderMobileMenu />
    </div>
  );
}

export default function Header() {
  return (
    <header className="relative z-20 border-b border-border bg-background px-4 flex justify-between">
      <nav className="mx-auto flex w-full min-w-0 max-w-5xl flex-nowrap items-center gap-4 p-3 sm:gap-6">
        <HeaderBrandLink />
        <HeaderPrimaryNav />
        <HeaderAccountControls />
      </nav>
    </header>
  );
}
